/**
 * Teslyn Cloud's own public air traffic (Diurnal parity C7, item 03-017; decision 627: "cloud polling is needed so
 * the cloud maps can have public air traffic"; docs/specs/cloud/public-traffic.md). A tenant admin switches it on
 * (off by default); then, while a staff member has the Operations Hub or a project's map open, Teslyn Cloud asks at
 * once and then as often as Teslyn Field asks: Community ADS-B about each active project's area every 5 minutes, and
 * the Oz Runways and AvPlan feeds every 2 minutes for every area (decision 826); it keeps 2 hours of positions
 * (decision 825), so the maps draw each
 * aircraft's trail (MB, 27 Oct 2026: the cloud maps were slow and had no trails). Public air traffic is for staff
 * only, on every map;
 * clients never see it (decision 765). Teslyn Field keeps asking around each laptop's own map view as before
 * (decision 65); nothing here changes it.
 *
 * Sources (spec §2): adsb.lol (free, ODbL, credited on the map), with adsb.fi after it only for organisations whose
 * adsb.fi switch is on (decision 405), as Teslyn Field does; Oz Runways and AvPlan (decisions 65 and 765: public
 * feeds that may be polled from the cloud), each one request of the whole world a run, asked whenever an
 * organisation has cloud traffic on (no organisation switch exists for them: in Teslyn Field they are each user's
 * map tick, decisions 357, 358). OpenSky stays out of the cloud (decision 765). The same readers as Teslyn Field
 * (`parseAdsbLol`, `parseOzRunways`, `parseAvPlan`), the same request addresses as its `public_traffic_fetch`, the
 * same waits after a refusal (`trafficCooldownMs`, `retryAfterMs`).
 *
 * The Edge Function `traffic-poller` runs a JavaScript build of this module and its imports
 * (`node tools/dev/copy-edge-shared.mjs` writes supabase/functions/_shared/domain/). Keep every type of an exported
 * signature declared in this file, so the build's declaration file imports nothing.
 */
import {
  PUBLIC_TRAFFIC_SOURCES,
  nmBetween,
  parseAdsbLol,
  parseAvPlan,
  parseOzRunways,
  retryAfterMs,
  trafficCooldownMs,
  viewCentre,
} from './public-traffic.js';
import { projectAreaBox } from './project-area.js';

// ---------------------------------------------------------------------------------------------- the rules

/**
 * Community ADS-B for each organisation's areas every 5 minutes, as Teslyn Field asks adsb.lol (decision 356: 288
 * requests in a whole day for one area); the first time at once when the organisation starts being watched.
 */
export const CLOUD_TRAFFIC_POLL_MS = 300_000;
/**
 * Oz Runways and AvPlan every 2 minutes (decision 826, MB: "let's go with 2 minutes"; Teslyn Field asks them every
 * 30 s from each laptop). One request of the whole world each.
 */
export const CLOUD_TRAFFIC_FEED_POLL_MS = 120_000;
/**
 * The schedule (pg_cron) runs the poller this often while watched: each minute, so a source is asked within its own
 * interval (the first time at once, from the watch).
 */
export const CLOUD_TRAFFIC_TICK_MS = 60_000;
/** After the first answer, a map reads the stored positions this often (the poller stores them each minute at most). */
export const CLOUD_TRAFFIC_READ_MS = 60_000;
/** A source is due this much before its interval is up, so the schedule's tick on the interval asks it. */
export const CLOUD_TRAFFIC_DUE_SLACK_MS = 10_000;
/** A fix is taken, and its aircraft drawn, while under 10 minutes old (two Community ADS-B polls). */
export const CLOUD_TRAFFIC_FRESH_MS = 600_000;
/** Positions are kept 2 hours for the aircraft's trails, then purged (decision 825). */
export const CLOUD_TRAFFIC_TRAIL_MS = 7_200_000;
/** While the first answer is on its way after a map opens, the map reads again this often, for this long. */
export const CLOUD_TRAFFIC_FIRST_READ_MS = 8_000;
export const CLOUD_TRAFFIC_FIRST_READS_FOR_MS = 40_000;
/** A Hub viewer keeps the organisation's traffic asked for this long after its last refresh (each minute). */
export const CLOUD_TRAFFIC_WATCH_MS = 600_000;
/**
 * The margin around a project's area, nautical miles: Teslyn Field's default radius for Community ADS-B (decision
 * 767, replacing decision 763's 50 km).
 */
export const CLOUD_TRAFFIC_MARGIN_NM = PUBLIC_TRAFFIC_SOURCES.adsb.radiusNm.default;
/** The project statuses asked about: Mobilising and In progress. */
export const CLOUD_TRAFFIC_STATUSES: readonly string[] = ['mobilising', 'active'];
/** adsb.lol and adsb.fi answer at most 250 nm around a point. */
export const CLOUD_TRAFFIC_MAX_RADIUS_NM = 250;
/**
 * Requests Teslyn Cloud sends a service in one UTC day, for every organisation together. adsb.lol allows 500 a day
 * to one address (its x-ratelimit-limit header); the Edge Functions' addresses may be shared, so the cloud keeps to
 * 400. adsb.fi is only the fallback. Oz Runways and AvPlan publish no limit: one request of the whole world every
 * 2 minutes (decision 826) is at most 720 in a whole day; 800 stops a schedule that runs too often.
 */
export const CLOUD_TRAFFIC_DAILY_CAP: Readonly<Record<CloudTrafficService, number>> = {
  'adsb.lol': 400,
  'adsb.fi': 200,
  ozrunways: 800,
  avplan: 800,
};
/** Areas asked in one run at most; the rest take their turn in the next runs. */
export const CLOUD_TRAFFIC_MAX_ASKS = 30;
/** The least time between two requests of a run: adsb.lol allows 4 a second and 60 a minute, adsb.fi 1 a second. */
export const CLOUD_TRAFFIC_SPACING_MS = 1_100;
/** How long a run holds the poller, so an overlapping run asks nothing. */
export const CLOUD_TRAFFIC_CLAIM_SECONDS = 240;
/** Teslyn Cloud's own name: never a browser's, and never a Referer or Origin (as Teslyn Field). */
export const CLOUD_TRAFFIC_USER_AGENT = 'TeslynCloud/1 (public traffic for the Operations Hub)';
/** A position this far ahead of the poller's clock is not kept. */
const AHEAD_MS = 60_000;

/** The feeds of the whole world, asked once a run for every area (Oz Runways and AvPlan, decision 765). */
export type CloudTrafficFeed = 'ozrunways' | 'avplan';
export const CLOUD_TRAFFIC_FEEDS: readonly CloudTrafficFeed[] = ['ozrunways', 'avplan'];

/** The services Teslyn Cloud asks: Community ADS-B by its services' names, and the two feeds. */
export type CloudTrafficService = 'adsb.lol' | 'adsb.fi' | CloudTrafficFeed;
export const CLOUD_TRAFFIC_SERVICES: readonly CloudTrafficService[] = [
  'adsb.lol',
  'adsb.fi',
  'ozrunways',
  'avplan',
];

/** A service in words ("Oz Runways"). */
export const CLOUD_TRAFFIC_SERVICE_NAME: Readonly<Record<CloudTrafficService, string>> = {
  'adsb.lol': 'adsb.lol',
  'adsb.fi': 'adsb.fi',
  ozrunways: PUBLIC_TRAFFIC_SOURCES.ozrunways.serviceName,
  avplan: PUBLIC_TRAFFIC_SOURCES.avplan.serviceName,
};

/** The credit each service asks for on the map (as Teslyn Field credits it). */
export const CLOUD_TRAFFIC_CREDIT: Readonly<Record<CloudTrafficService, string>> = {
  'adsb.lol': PUBLIC_TRAFFIC_SOURCES.adsb.credit,
  'adsb.fi': PUBLIC_TRAFFIC_SOURCES.adsb.fallback!.credit,
  ozrunways: PUBLIC_TRAFFIC_SOURCES.ozrunways.credit,
  avplan: PUBLIC_TRAFFIC_SOURCES.avplan.credit,
};

const nameOf = (s: CloudTrafficService): string => CLOUD_TRAFFIC_SERVICE_NAME[s];

// ---------------------------------------------------------------------------------------------- inputs

/** A block of a candidate project: its boundary in its projection. */
export interface TrafficBlock {
  readonly epsg?: number | null;
  readonly boundary?: ReadonlyArray<readonly [number, number]> | null;
  readonly deleted_at?: string | null;
}

/** An area of the project's signed contract (the quote snapshot's area). */
export interface TrafficContractArea {
  readonly name?: string;
  readonly zone?: number | null;
  readonly hemisphere?: 'N' | 'S' | null;
  readonly boundary?: ReadonlyArray<readonly [number, number]> | null;
  readonly boundary_table?: {
    readonly kind: 'grid' | 'geographic';
    readonly zone: number | null;
    readonly points: ReadonlyArray<readonly [number, number]>;
  } | null;
}

/** One project of `cloud_traffic_candidates()`: Mobilising or In progress, with what its area needs. */
export interface TrafficProjectRow {
  readonly id: string;
  readonly number: string;
  readonly epsg: number | null;
  readonly blocks: readonly TrafficBlock[] | null;
  readonly contract_areas: readonly TrafficContractArea[] | null;
}

/** One organisation of `cloud_traffic_candidates()`: switched on and watched now. */
export interface TrafficCandidate {
  readonly tenant_id: string;
  /** Its adsb.fi switch (decision 405): adsb.fi may be asked after adsb.lol. */
  readonly adsbfi: boolean;
  /** When Community ADS-B was last asked about its areas (`cloud_traffic_setting.adsb_at`); null: never. */
  readonly adsb_at?: string | null;
  readonly projects: readonly TrafficProjectRow[] | null;
}

/** A project's area as a circle around its box's centre: what adsb.lol is asked. */
export interface TrafficArea {
  readonly tenant_id: string;
  readonly project_id: string;
  readonly number: string;
  readonly lat: number;
  readonly lon: number;
  readonly radius_nm: number;
  /** The area and its margin are wider than the services answer: only its middle is asked. */
  readonly clipped: boolean;
  readonly adsbfi: boolean;
}

/** One request: a circle that covers one or more areas (of any organisation). */
export interface TrafficAsk {
  readonly lat: number;
  readonly lon: number;
  readonly radius_nm: number;
  readonly areas: readonly TrafficArea[];
}

/** What the poller knows of a service (`cloud_traffic_service`, one row each). */
export interface TrafficServiceRow {
  readonly service: CloudTrafficService;
  /** The UTC day `used` counts, `YYYY-MM-DD`; null before the first request. */
  readonly day: string | null;
  readonly used: number;
  readonly fails: number;
  readonly retry_at: string | null;
  readonly last_status: number | null;
  readonly last_at: string | null;
}

/** One position for `cloud_traffic_position`. */
export interface TrafficPositionRow {
  readonly tenant_id: string;
  readonly project_id: string;
  /** Community ADS-B, Oz Runways or AvPlan (Teslyn Field's source ids). */
  readonly source: 'adsb' | CloudTrafficFeed;
  readonly service: CloudTrafficService;
  readonly source_id: string;
  readonly label: string;
  readonly registration: string;
  readonly icao_hex: string;
  readonly kind: 'fixed_wing' | 'helicopter' | 'ground' | null;
  readonly lat: number;
  readonly lon: number;
  readonly alt_ft: number | null;
  readonly speed_kt: number | null;
  readonly heading_deg: number | null;
  readonly fix_time: string;
  /** What the service says (ADS-B's own ground flag or an altitude); null when it does not say. */
  readonly airborne: boolean | null;
}

/** An organisation's result of a run, for `cloud_traffic_setting` (the Hub shows the words). */
export interface TrafficTenantState {
  readonly tenant_id: string;
  readonly state: 'ok' | 'waiting' | 'error' | 'no_area';
  readonly message: string;
  readonly aircraft: number;
  readonly polled_at: string;
}

// ---------------------------------------------------------------------------------------------- areas

const iso = (ms: number): string => new Date(ms).toISOString();

/**
 * The circle asked for one project's area: the centre of the box of its block boundaries (else its contract's
 * survey areas, as the Operations Hub outlines it), out to the box's farthest corner plus the margin, in whole
 * nautical miles (1 to 250). Null without a boundary or contract area.
 */
export function trafficCircle(
  p: Pick<TrafficProjectRow, 'epsg' | 'blocks' | 'contract_areas'>,
  marginNm: number = CLOUD_TRAFFIC_MARGIN_NM,
): { lat: number; lon: number; radius_nm: number; clipped: boolean } | null {
  const box = projectAreaBox({ epsg: p.epsg, blocks: p.blocks ?? [], contractAreas: p.contract_areas });
  return box ? trafficCircleOfBox(box, marginNm) : null;
}

/**
 * The circle of `trafficCircle` round a box already in latitude and longitude (Teslyn Mobile's block boundaries,
 * `phoneTrafficCircle`).
 */
export function trafficCircleOfBox(
  box: { readonly south: number; readonly west: number; readonly north: number; readonly east: number },
  marginNm: number = CLOUD_TRAFFIC_MARGIN_NM,
): { lat: number; lon: number; radius_nm: number; clipped: boolean } {
  const c = viewCentre(box);
  const far = Math.max(
    ...[
      { lat: box.south, lon: box.west },
      { lat: box.south, lon: box.east },
      { lat: box.north, lon: box.west },
      { lat: box.north, lon: box.east },
    ].map((corner) => nmBetween(c, corner)),
  );
  const wanted = Math.ceil(far + marginNm);
  return {
    lat: Math.round(c.lat * 10_000) / 10_000,
    lon: Math.round(c.lon * 10_000) / 10_000,
    radius_nm: Math.min(CLOUD_TRAFFIC_MAX_RADIUS_NM, Math.max(1, wanted)),
    clipped: wanted > CLOUD_TRAFFIC_MAX_RADIUS_NM,
  };
}

/** Every area to ask (the organisations' Mobilising and In progress projects), and the projects without one. */
export function trafficAreas(candidates: readonly TrafficCandidate[]): {
  areas: TrafficArea[];
  without: { tenant_id: string; project_id: string; number: string }[];
} {
  const areas: TrafficArea[] = [];
  const without: { tenant_id: string; project_id: string; number: string }[] = [];
  for (const t of candidates) {
    for (const p of t.projects ?? []) {
      const c = trafficCircle(p);
      if (!c) without.push({ tenant_id: t.tenant_id, project_id: p.id, number: p.number });
      else areas.push({ tenant_id: t.tenant_id, project_id: p.id, number: p.number, adsbfi: t.adsbfi, ...c });
    }
  }
  return { areas, without };
}

/**
 * The requests of a run: one circle per area, except that an area wholly inside a larger one already asked (of the
 * same or another organisation) rides on that request. Largest first; at most `max` requests, the turn rotating
 * with the run (`nowMs`), so every area is asked within a few runs when there are more.
 */
export function trafficAsks(
  areas: readonly TrafficArea[],
  nowMs: number,
  max: number = CLOUD_TRAFFIC_MAX_ASKS,
): { asks: TrafficAsk[]; later: TrafficArea[] } {
  const sorted = [...areas].sort(
    (a, b) =>
      b.radius_nm - a.radius_nm ||
      (a.tenant_id < b.tenant_id ? -1 : a.tenant_id > b.tenant_id ? 1 : 0) ||
      (a.project_id < b.project_id ? -1 : a.project_id > b.project_id ? 1 : 0),
  );
  const asks: { lat: number; lon: number; radius_nm: number; areas: TrafficArea[] }[] = [];
  for (const a of sorted) {
    const host = asks.find((k) => nmBetween(k, a) + a.radius_nm <= k.radius_nm);
    if (host) host.areas.push(a);
    else asks.push({ lat: a.lat, lon: a.lon, radius_nm: a.radius_nm, areas: [a] });
  }
  if (asks.length <= max) return { asks, later: [] };
  const start = (Math.floor(nowMs / CLOUD_TRAFFIC_POLL_MS) * max) % asks.length;
  const turn = [...asks.slice(start), ...asks.slice(0, start)];
  return { asks: turn.slice(0, max), later: turn.slice(max).flatMap((k) => k.areas) };
}

/** The whole-world address of a feed, exactly as Teslyn Field's `public_traffic_fetch` asks it. */
export function feedUrl(feed: CloudTrafficFeed): string {
  return feed === 'ozrunways'
    ? 'https://tx.ozrunways.com/tx/geo'
    : 'https://avplan-efb.firebaseio.com/live.json';
}

/** The address asked, exactly as Teslyn Field's `public_traffic_fetch` builds it (src-tauri/src/traffic.rs). */
export function trafficUrl(
  service: 'adsb.lol' | 'adsb.fi',
  ask: Pick<TrafficAsk, 'lat' | 'lon' | 'radius_nm'>,
): string {
  const lat = ask.lat.toFixed(4);
  const lon = ask.lon.toFixed(4);
  const r = Math.min(CLOUD_TRAFFIC_MAX_RADIUS_NM, Math.max(1, Math.round(ask.radius_nm)));
  return service === 'adsb.lol'
    ? `https://api.adsb.lol/v2/point/${lat}/${lon}/${r}`
    : `https://opendata.adsb.fi/api/v3/lat/${lat}/lon/${lon}/dist/${r}`;
}

// ---------------------------------------------------------------------------------------------- the services

const dayOf = (ms: number): string => iso(ms).slice(0, 10);
const hhmm = (ms: number): string => iso(ms).slice(11, 16);

/** A service's row before its first request. */
export function freshService(service: CloudTrafficService): TrafficServiceRow {
  return { service, day: null, used: 0, fails: 0, retry_at: null, last_status: null, last_at: null };
}

/** Requests still allowed today (UTC). */
export function serviceAllowance(row: TrafficServiceRow, nowMs: number): number {
  const used = row.day === dayOf(nowMs) ? row.used : 0;
  return Math.max(0, CLOUD_TRAFFIC_DAILY_CAP[row.service] - used);
}

/** Whether a service may be asked now; the words for the organisations when not. */
export function serviceReady(
  row: TrafficServiceRow,
  nowMs: number,
): { readonly ready: true } | { readonly ready: false; readonly state: 'waiting'; readonly why: string } {
  const until = row.retry_at ? Date.parse(row.retry_at) : NaN;
  if (Number.isFinite(until) && until > nowMs)
    return {
      ready: false,
      state: 'waiting',
      why: `${nameOf(row.service)} asked Teslyn Cloud to wait; asked again after ${hhmm(until)} UTC.`,
    };
  if (serviceAllowance(row, nowMs) <= 0)
    return {
      ready: false,
      state: 'waiting',
      why: `Teslyn Cloud has used today's ${nameOf(row.service)} requests; asked again after midnight UTC.`,
    };
  return { ready: true };
}

/**
 * A service's row after one request (counted whatever the answer): an answer with its aircraft list clears the
 * failures; a refusal (429, 403) waits the service's Retry-After or 1, 2, 4 … minutes up to 15 (403 at least 5),
 * as Teslyn Field; any other failure waits until the next run. `status` is null when the service was not reached.
 */
export function serviceAfter(
  row: TrafficServiceRow,
  outcome: { readonly status: number | null; readonly ok: boolean; readonly retryAfter?: string | null },
  nowMs: number,
): TrafficServiceRow {
  const today = dayOf(nowMs);
  const used = (row.day === today ? row.used : 0) + 1;
  const base = { ...row, day: today, used, last_status: outcome.status, last_at: iso(nowMs) };
  if (outcome.ok) return { ...base, fails: 0, retry_at: null };
  const fails = row.fails + 1;
  const status = outcome.status ?? 0;
  if (status !== 429 && status !== 403) return { ...base, fails, retry_at: null };
  const wait = trafficCooldownMs(
    status,
    fails,
    retryAfterMs(outcome.retryAfter ?? null, nowMs),
    row.service === 'ozrunways' || row.service === 'avplan'
      ? CLOUD_TRAFFIC_FEED_POLL_MS
      : CLOUD_TRAFFIC_POLL_MS,
    PUBLIC_TRAFFIC_SOURCES[row.service === 'ozrunways' || row.service === 'avplan' ? row.service : 'adsb']
      .backoff,
  );
  return { ...base, fails, retry_at: iso(nowMs + wait) };
}

const isDue = (at: string | null | undefined, nowMs: number, everyMs: number): boolean => {
  const ms = at ? Date.parse(at) : NaN;
  return !Number.isFinite(ms) || nowMs - ms >= everyMs - CLOUD_TRAFFIC_DUE_SLACK_MS;
};

/**
 * What one run asks (MB, 27 Oct 2026: as often as Teslyn Field asks, not every 5 minutes for everything): Community
 * ADS-B for the organisations whose areas were last asked 5 minutes ago or more, or never (the first watch asks at
 * once); Oz Runways and AvPlan when last asked 2 minutes ago or more (under 110 s is never due, so a run the first watch
 * adds just after the schedule's own asks nothing again). A wait or a used-up day stays `serviceReady`'s rule.
 */
export function trafficDue(input: {
  readonly candidates: readonly Pick<TrafficCandidate, 'tenant_id' | 'adsb_at'>[];
  readonly services: readonly Pick<TrafficServiceRow, 'service' | 'last_at'>[];
  readonly nowMs: number;
}): { tenants: Set<string>; feeds: CloudTrafficFeed[] } {
  const last = new Map(input.services.map((s) => [s.service, s.last_at]));
  return {
    tenants: new Set(
      input.candidates
        .filter((c) => isDue(c.adsb_at, input.nowMs, CLOUD_TRAFFIC_POLL_MS))
        .map((c) => c.tenant_id),
    ),
    feeds: CLOUD_TRAFFIC_FEEDS.filter((f) => isDue(last.get(f), input.nowMs, CLOUD_TRAFFIC_FEED_POLL_MS)),
  };
}

/** Why a request gave no aircraft list, in words for the organisation. */
export function serviceProblem(
  service: CloudTrafficService,
  outcome: { readonly status: number | null; readonly problem?: string | null },
): string {
  const name = nameOf(service);
  if (outcome.status === null) return `${name} could not be reached.`;
  if (outcome.status === 429) return `${name} asked Teslyn Cloud to slow down (HTTP 429).`;
  if (outcome.status !== 200) return `${name} answered HTTP ${outcome.status}.`;
  return outcome.problem ?? `${name} sent an answer Teslyn cannot read.`;
}

// ---------------------------------------------------------------------------------------------- answers

const KINDS: readonly string[] = ['fixed_wing', 'helicopter', 'ground'];

/** Positions as rows: inside each area's own circle, under 10 minutes old, once per organisation and fix. */
function keepRows(
  positions: readonly {
    readonly deviceId: string;
    readonly label: string;
    readonly registration: string | null;
    readonly icaoHex: string | null;
    readonly kind: string | null;
    readonly lat: number;
    readonly lon: number;
    readonly timeMs: number;
    readonly altFt: number | null;
    readonly speedKt: number | null;
    readonly headingDeg: number | null;
    readonly providerFlying: boolean | null;
  }[],
  source: TrafficPositionRow['source'],
  service: CloudTrafficService,
  areas: readonly TrafficArea[],
  nowMs: number,
): TrafficPositionRow[] {
  const rows: TrafficPositionRow[] = [];
  const seen = new Set<string>();
  for (const area of areas) {
    for (const p of positions) {
      if (p.timeMs < nowMs - CLOUD_TRAFFIC_FRESH_MS || p.timeMs > nowMs + AHEAD_MS) continue;
      if (nmBetween(area, p) > area.radius_nm) continue;
      const id = p.deviceId.slice(0, 32);
      const key = `${area.tenant_id}|${id}|${p.timeMs}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        tenant_id: area.tenant_id,
        project_id: area.project_id,
        source,
        service,
        source_id: id,
        label: p.label.slice(0, 64),
        registration: (p.registration ?? '').slice(0, 16),
        icao_hex: p.icaoHex ?? '',
        kind: p.kind && KINDS.includes(p.kind) ? (p.kind as TrafficPositionRow['kind']) : null,
        lat: p.lat,
        lon: p.lon,
        alt_ft: p.altFt,
        speed_kt: p.speedKt,
        heading_deg: p.headingDeg,
        fix_time: iso(p.timeMs),
        airborne: p.providerFlying,
      });
    }
  }
  return rows;
}

/**
 * The rows one Community ADS-B answer gives: every aircraft of the answer inside each area's own circle whose
 * position is under 10 minutes old, for that area's organisation and project. An aircraft seen around two projects
 * of one organisation is kept once (the first area). `problem` is the reader's words when the answer has no
 * aircraft list.
 */
export function trafficRows(
  body: string,
  service: 'adsb.lol' | 'adsb.fi',
  ask: Pick<TrafficAsk, 'areas'>,
  nowMs: number,
): { rows: TrafficPositionRow[]; problem: string | null } {
  const parsed = parseAdsbLol(body, service);
  if (parsed.problem) return { rows: [], problem: parsed.problem };
  return { rows: keepRows(parsed.value, 'adsb', service, ask.areas, nowMs), problem: null };
}

/**
 * The rows one whole-world feed answer gives (Oz Runways or AvPlan, read as Teslyn Field reads them: no simulators,
 * none the pilot asked not to show, AvPlan's callsign as the aircraft's id), kept for every area as `trafficRows`
 * keeps them. `nowMs` is when the answer came (Oz Runways sends each position's age).
 */
export function feedRows(
  body: string,
  feed: CloudTrafficFeed,
  areas: readonly TrafficArea[],
  nowMs: number,
): { rows: TrafficPositionRow[]; problem: string | null } {
  const parsed = feed === 'ozrunways' ? parseOzRunways(body, nowMs) : parseAvPlan(body, nowMs);
  if (parsed.problem) return { rows: [], problem: parsed.problem };
  return { rows: keepRows(parsed.value, feed, feed, areas, nowMs), problem: null };
}

// ---------------------------------------------------------------------------------------------- words

/** What happened to one area's Community ADS-B request in a run. */
export interface TrafficAreaOutcome {
  readonly area: TrafficArea;
  /** The service whose answer was read; null when none answered. */
  readonly service: 'adsb.lol' | 'adsb.fi' | null;
  /** Why the area has no answer, or why adsb.fi answered instead of adsb.lol. */
  readonly problem: string | null;
  readonly state: 'ok' | 'waiting' | 'error';
}

/** What happened to a whole-world feed in a run (one request for every area). */
export interface TrafficFeedOutcome {
  readonly feed: CloudTrafficFeed;
  /** Why it has no answer; null when its answer was read. */
  readonly problem: string | null;
  readonly state: 'ok' | 'waiting' | 'error';
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * Each organisation's result of a run, in words for the Hub: "adsb.lol: 7 aircraft around 2 projects. Oz Runways:
 * 2 aircraft. AvPlan: 4 aircraft.", with the reason when adsb.fi answered instead, the projects not asked and why,
 * a feed's problem, and the projects without an area. `aircraft` adds up each source's aircraft.
 */
export function trafficTenantStates(input: {
  readonly candidates: readonly TrafficCandidate[];
  readonly outcomes: readonly TrafficAreaOutcome[];
  /** The whole-world feeds of the run (none: not asked). */
  readonly feeds?: readonly TrafficFeedOutcome[];
  readonly without: readonly { readonly tenant_id: string; readonly number: string }[];
  readonly rows: readonly TrafficPositionRow[];
  readonly nowMs: number;
}): TrafficTenantState[] {
  return input.candidates.map((t) => {
    const mine = input.outcomes.filter((o) => o.area.tenant_id === t.tenant_id);
    const none = input.without.filter((w) => w.tenant_id === t.tenant_id).map((w) => w.number);
    const noneText =
      none.length > 0 ? ` ${none.join(', ')}: no block boundary or contract area to ask around.` : '';
    const base = { tenant_id: t.tenant_id, polled_at: iso(input.nowMs) };
    if (mine.length === 0)
      return {
        ...base,
        state: 'no_area' as const,
        aircraft: 0,
        message:
          none.length > 0
            ? `No project in progress has a block boundary or contract area to ask around (${none.join(', ')}).`
            : 'No project is mobilising or in progress.',
      };
    const answered = mine.filter((o) => o.service !== null);
    const ours = input.rows.filter((r) => r.tenant_id === t.tenant_id);
    const count = (source: TrafficPositionRow['source']): number =>
      new Set(ours.filter((r) => r.source === source).map((r) => r.source_id)).size;
    const feeds = input.feeds ?? [];
    const aircraft = count('adsb') + feeds.reduce((n, f) => n + (f.problem === null ? count(f.feed) : 0), 0);
    const feedText = feeds
      .map((f) =>
        f.problem === null
          ? ` ${nameOf(f.feed)}: ${plural(count(f.feed), 'aircraft', 'aircraft')}.`
          : ` ${f.problem}`,
      )
      .join('');
    const feedOk = feeds.some((f) => f.problem === null);
    const clipped = mine.filter((o) => o.area.clipped).map((o) => o.area.number);
    const clippedText =
      clipped.length > 0
        ? ` ${clipped.join(', ')}: wider than ${CLOUD_TRAFFIC_MAX_RADIUS_NM} nm, only its middle is asked.`
        : '';
    if (answered.length === 0) {
      const first = mine[0]!;
      return {
        ...base,
        state: feedOk
          ? ('ok' as const)
          : first.state === 'waiting'
            ? ('waiting' as const)
            : ('error' as const),
        aircraft,
        message: `${first.problem ?? 'Not asked.'}${feedText}${noneText}`,
      };
    }
    const services = [...new Set(answered.map((o) => o.service!))];
    const lead = services
      .map((s) => {
        const via = answered.find((o) => o.service === s && o.problem);
        return s === 'adsb.fi' && via?.problem ? `adsb.fi (${via.problem.replace(/\.$/, '')})` : s;
      })
      .join(' and ');
    const missed = mine.filter((o) => o.service === null);
    const missedText =
      missed.length > 0
        ? ` ${missed.map((o) => o.area.number).join(', ')} not asked: ${missed[0]!.problem ?? 'its turn is next.'}`
        : '';
    return {
      ...base,
      state: 'ok' as const,
      aircraft,
      message: `${lead}: ${plural(count('adsb'), 'aircraft', 'aircraft')} around ${plural(answered.length, 'project', 'projects')}.${missedText}${clippedText}${feedText}${noneText}`,
    };
  });
}

// ---------------------------------------------------------------------------------------------- the Hub

/** The organisation's setting and last result, as `cloud_traffic_watch` returns it. */
export interface CloudTrafficStatus {
  readonly enabled: boolean;
  readonly state: string;
  readonly message: string;
  readonly aircraft: number;
  readonly polled_at: string | null;
  /** The organisation's adsb.fi switch (decision 405); absent before 20261027003500. */
  readonly adsbfi?: boolean;
  /** Each service's stored row (`cloud_traffic_service`); absent before 20261027003500. */
  readonly sources?: readonly TrafficServiceRow[];
  /** This watch asked the poller to run now (the organisation was not watched); absent before 20261027014000. */
  readonly kicked?: boolean;
}

/** One position as `ops_hub_public_traffic` returns it. */
export interface PublicTrafficHubRow {
  /** The row's id (since 20261027014000): a map reads only the rows after the last one it has. */
  readonly id?: number;
  /** The project whose area the position was asked around. */
  readonly project_id?: string;
  readonly source: string;
  readonly service: string;
  readonly source_id: string;
  readonly label: string;
  readonly registration: string;
  readonly icao_hex: string;
  readonly kind: string | null;
  readonly lat: number;
  readonly lon: number;
  readonly alt_ft: number | null;
  readonly speed_kt: number | null;
  readonly heading_deg: number | null;
  readonly fix_time: string;
  readonly airborne: boolean | null;
}

/** The Hub's line about public air traffic: off, waiting for the first answer, or the poller's words and age. */
export function cloudTrafficStatusText(s: CloudTrafficStatus | null, nowMs: number): string {
  if (!s || !s.enabled) return 'Public air traffic: off. A tenant admin can turn it on in Settings.';
  const at = s.polled_at ? Date.parse(s.polled_at) : NaN;
  if (!Number.isFinite(at))
    return 'Public air traffic: on. Teslyn Cloud is asking now; the first aircraft show within a minute.';
  const min = Math.max(0, Math.round((nowMs - at) / 60_000));
  const age = min < 1 ? 'just now' : `${min} min ago`;
  return `Public air traffic: ${s.message} Asked ${age}.`;
}

/**
 * How long a map waits before reading public traffic again: every 8 s for the first 40 s after it opened (the first
 * answer is on its way: the watch asked the poller to run at once), then each minute, as often as the poller can
 * store new positions (Oz Runways and AvPlan every 2 minutes, adsb.lol every 5).
 */
export function cloudTrafficReadDelayMs(sinceOpenMs: number): number {
  return sinceOpenMs < CLOUD_TRAFFIC_FIRST_READS_FOR_MS ? CLOUD_TRAFFIC_FIRST_READ_MS : CLOUD_TRAFFIC_READ_MS;
}

/**
 * A map's rows after a read (20261027014000: a map that has the trails reads only the rows stored since): the rows it
 * had and the new ones, each fix once, none older than the 2-hour trail; and the id to read after next time (null:
 * read everything again, as before the migration, when rows carry no id).
 */
export function mergePublicRows<T extends PublicTrafficHubRow>(
  had: readonly T[],
  got: readonly T[],
  nowMs: number,
): { rows: T[]; after: number | null } {
  const seen = new Set<string>();
  const rows: T[] = [];
  for (const r of [...had, ...got]) {
    const k = `${r.source}|${r.source_id}|${r.fix_time}`;
    if (seen.has(k) || !(Date.parse(r.fix_time) > nowMs - CLOUD_TRAFFIC_TRAIL_MS)) continue;
    seen.add(k);
    rows.push(r);
  }
  const ids = [...had, ...got].map((r) => Number(r.id));
  const after = ids.length > 0 && ids.every(Number.isFinite) ? Math.max(...ids) : null;
  return { rows, after };
}

/**
 * The public aircraft for one project's map (staff only): the positions inside the project's own circle (its area
 * and the 100 nm margin, `trafficCircle`), whichever project's request found them. No circle: none.
 */
export function publicRowsNear<T extends Pick<PublicTrafficHubRow, 'lat' | 'lon'>>(
  rows: readonly T[],
  circle: { readonly lat: number; readonly lon: number; readonly radius_nm: number } | null,
): T[] {
  if (!circle) return [];
  return rows.filter((r) => nmBetween(circle, r) <= circle.radius_nm);
}

/** The credits the map shows for the services whose positions it draws. */
export function cloudTrafficCredits(rows: readonly Pick<PublicTrafficHubRow, 'service'>[]): string[] {
  return CLOUD_TRAFFIC_SERVICES.filter((s) => rows.some((r) => r.service === s)).map(
    (s) => CLOUD_TRAFFIC_CREDIT[s],
  );
}

// ---------------------------------------------------------------------------------------------- the source strip

/** A project of the Hub as the strip needs it (its area, as the poller draws it). */
export interface HubTrafficProject {
  readonly id: string;
  readonly number: string;
  readonly status: string;
  readonly epsg: number | null;
  readonly blocks: readonly TrafficBlock[];
  readonly contractAreas: readonly TrafficContractArea[] | null;
}

/** The circles Teslyn Cloud asks for the Hub's projects (Mobilising and In progress), as the poller makes them. */
export function hubTrafficAreas(projects: readonly HubTrafficProject[]): TrafficArea[] {
  return trafficAreas([
    {
      tenant_id: '',
      adsbfi: false,
      projects: projects
        .filter((p) => CLOUD_TRAFFIC_STATUSES.includes(p.status))
        .map((p) => ({
          id: p.id,
          number: p.number,
          epsg: p.epsg,
          blocks: p.blocks,
          contract_areas: p.contractAreas,
        })),
    },
  ]).areas;
}

/** How a source is doing: answering, waiting (it asked to wait, or the day is used up), failing, standing by, off. */
export type HubSourceState = 'ok' | 'waiting' | 'failed' | 'standby' | 'off';

/** The word beside each dot, so the state never rests on colour alone. */
export const HUB_SOURCE_WORD: Readonly<Record<HubSourceState, string>> = {
  ok: 'Answering',
  waiting: 'Waiting',
  failed: 'Not answering',
  standby: 'Standby',
  off: 'Off',
};

/** One item of the Hub's strip of public traffic sources. */
export interface HubSourceItem {
  readonly service: CloudTrafficService;
  readonly name: string;
  readonly state: HubSourceState;
  readonly word: string;
  /** Aircraft of this source on the map now. */
  readonly aircraft: number;
  /** "asked 2 min ago"; null when never asked. */
  readonly age: string | null;
  /** What the pointer shows: the aircraft within each project's circle, the wait, the failure, or why it is off. */
  readonly title: string;
}

const ageWords = (nowMs: number, at: string | null): string | null => {
  const ms = at ? Date.parse(at) : NaN;
  if (!Number.isFinite(ms)) return null;
  const min = Math.max(0, Math.round((nowMs - ms) / 60_000));
  return min < 1 ? 'asked just now' : `asked ${min} min ago`;
};

const waitWords = (ms: number): string => {
  const s = Math.max(1, Math.round(ms / 1000));
  return s < 120 ? `${s} s` : `${Math.round(s / 60)} min`;
};

/**
 * The Hub's strip of public traffic sources (MB, 27 Oct 2026: "the operations hub has no feedback if the public feeds
 * are working"; the Diurnal tool's source dots): adsb.lol, adsb.fi, Oz Runways and AvPlan, each with its state from
 * the stored service row, its aircraft on the map and when it was last asked, and words for the pointer. Off for
 * every source while the organisation has public air traffic off; adsb.fi off without its switch and on standby
 * while adsb.lol answers (it is asked only when adsb.lol cannot answer).
 */
export function hubSourceStrip(input: {
  readonly status: CloudTrafficStatus | null;
  readonly rows: readonly PublicTrafficHubRow[];
  readonly areas: readonly Pick<TrafficArea, 'project_id' | 'number' | 'radius_nm'>[];
  readonly nowMs: number;
}): HubSourceItem[] {
  const st = input.status;
  const stored = new Map((st?.sources ?? []).map((r) => [r.service, r]));
  const within = (service: CloudTrafficService): string => {
    const mine = input.rows.filter((r) => r.service === service);
    const parts = input.areas.map((a) => {
      const n = new Set(
        mine.filter((r) => r.project_id === a.project_id).map((r) => `${r.source}:${r.source_id}`),
      ).size;
      return `${plural(n, 'aircraft', 'aircraft')} within ${a.radius_nm} nm of ${a.number}`;
    });
    return parts.length > 0 ? parts.join('; ') : 'No project is mobilising or in progress.';
  };
  return CLOUD_TRAFFIC_SERVICES.map((service): HubSourceItem => {
    const name = nameOf(service);
    const row = stored.get(service) ?? freshService(service);
    const aircraft = new Set(
      input.rows.filter((r) => r.service === service).map((r) => `${r.source}:${r.source_id}`),
    ).size;
    const age = ageWords(input.nowMs, row.last_at);
    const item = (state: HubSourceState, title: string): HubSourceItem => ({
      service,
      name,
      state,
      word: HUB_SOURCE_WORD[state],
      aircraft,
      age,
      title,
    });
    if (!st?.enabled) return item('off', 'Off: a tenant admin turns public air traffic on in Settings.');
    if (service === 'adsb.fi' && !st.adsbfi)
      return item('off', 'Off: adsb.fi is not set up for your organisation.');
    const until = row.retry_at ? Date.parse(row.retry_at) : NaN;
    if (Number.isFinite(until) && until > input.nowMs)
      return item('waiting', `${name} asked Teslyn Cloud to wait ${waitWords(until - input.nowMs)}.`);
    if (serviceAllowance(row, input.nowMs) <= 0)
      return item(
        'waiting',
        `Teslyn Cloud has used today's ${name} requests; asked again after midnight UTC.`,
      );
    if (service === 'adsb.fi') {
      const lol = stored.get('adsb.lol');
      if (row.last_at === null || (lol?.last_status === 200 && lol.fails === 0))
        return item('standby', 'Asked only when adsb.lol cannot answer.');
    }
    if (row.last_at === null)
      return item(
        'waiting',
        'Not asked yet: Teslyn Cloud asks within a minute while the Operations Hub or a project map is open.',
      );
    if (row.last_status === 200 && row.fails === 0) return item('ok', within(service));
    return item('failed', serviceProblem(service, { status: row.last_status }));
  });
}
